import React, { useState, useEffect, useMemo, useCallback } from "react"
import { Table, Col, Row } from "reactstrap"
import api from "API/Api"
import { Link } from "react-router-dom"
import SweetAlert from "react-bootstrap-sweetalert"
import loader from "./../../assets/loading/loader.gif"
import { usePermissions } from "API/PermissionContext"
import BulkImportExport from "../../components/BulkImportExport"

const Routes = () => {
  const { canAdd, canEdit, canDelete, canExport, MODULE } = usePermissions()
  const MID = MODULE.ROUTES
  const [routes, setRoutes] = useState([])
  const [dataLoaded, setDataLoaded] = useState(false)
  const [checkedItems, setCheckedItems] = useState({})
  const [parentChecked, setParentChecked] = useState(false)
  const [confirm_alert, setconfirm_alert] = useState(false)
  const [confirm_multiple_alert, setconfirm_multiple_alert] = useState(false)
  const [success_dlg, setsuccess_dlg] = useState(false)
  const [dynamic_title, setdynamic_title] = useState("")
  const [dynamic_description, setdynamic_description] = useState("")
  const [routeId, setRouteId] = useState("")
  const [search, setSearch] = useState("")
  const [showFilter, setShowFilter] = useState(false)
  const [filterCompany, setFilterCompany] = useState("")
  const [filterPlant, setFilterPlant] = useState("")
  const [filterCC, setFilterCC] = useState("")
  const [filterRouteName, setFilterRouteName] = useState("")
  const [filterRouteNumber, setFilterRouteNumber] = useState("")

  const fetchData = useCallback(async () => {
    try {
      const resp = await api.get("routes")
      setRoutes(Array.isArray(resp.data.data) ? resp.data.data : resp.data.data?.data || [])
    } catch (e) { console.error(e) }
    setDataLoaded(true)
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  const handleDelete = async () => {
    try { await api.delete("delete-route/" + routeId); fetchData() } catch (e) { console.error(e) }
  }
  const handleMultiDelete = async () => {
    const ids = Object.keys(checkedItems).filter(k => checkedItems[k])
    if (!ids.length) return
    try { await api.post("delete-multiple-routes", { items: ids }); setCheckedItems({}); setParentChecked(false); fetchData() } catch (e) { console.error(e) }
  }
  const handleCheckbox = id => {
    setCheckedItems(prev => {
      const n = { ...prev, [id]: !prev[id] }
      setParentChecked(routes.length > 0 && routes.every(r => n[r.id]))
      return n
    })
  }
  const handleParentCheckbox = () => {
    const n = !parentChecked; setParentChecked(n)
    const items = {}; routes.forEach(r => { items[r.id] = n }); setCheckedItems(items)
  }
  const selectedCount = Object.values(checkedItems).filter(Boolean).length

  // Unique filter options
  const uniqueCompanies = useMemo(() => [...new Set(routes.map(r => r.companyName).filter(Boolean))].sort(), [routes])
  const uniquePlants = useMemo(() => [...new Set(routes.map(r => r.plantName).filter(Boolean))].sort(), [routes])
  const uniqueCCs = useMemo(() => [...new Set(routes.map(r => r.centerName).filter(Boolean))].sort(), [routes])

  const filtered = useMemo(() => {
    return routes.filter(r => {
      if (filterCompany && r.companyName !== filterCompany) return false
      if (filterPlant && r.plantName !== filterPlant) return false
      if (filterCC && r.centerName !== filterCC) return false
      if (filterRouteName && !(r.routeName || "").toLowerCase().includes(filterRouteName.toLowerCase())) return false
      if (filterRouteNumber && !(r.routeNumber || "").toLowerCase().includes(filterRouteNumber.toLowerCase())) return false
      if (search) {
        const s = search.toLowerCase()
        return (r.routeName || "").toLowerCase().includes(s) ||
          (r.routeNumber || "").toLowerCase().includes(s) ||
          (r.companyName || "").toLowerCase().includes(s) ||
          (r.plantName || "").toLowerCase().includes(s) ||
          (r.centerName || "").toLowerCase().includes(s)
      }
      return true
    })
  }, [routes, search, filterCompany, filterPlant, filterCC, filterRouteName, filterRouteNumber])

  const removeFilter = () => {
    setFilterCompany(""); setFilterPlant(""); setFilterCC(""); setFilterRouteName(""); setFilterRouteNumber("")
    setShowFilter(!showFilter)
  }

  const summaryCards = [
    { label: "Total Routes", value: routes.length, icon: "fa-route", color: "#6366f1" },
    { label: "Companies", value: new Set(routes.map(r => r.companyName).filter(Boolean)).size, icon: "fa-building", color: "#0ea5e9" },
    { label: "Centers", value: new Set(routes.map(r => r.centerName).filter(Boolean)).size, icon: "fa-warehouse", color: "#22c55e" },
  ]

  // CSV Export
  const exportCSV = () => {
    if (!filtered.length) return
    const headers = ["#", "Company", "Plant", "Chilling Center", "Route Number", "Route Name"]
    const rows = filtered.map((r, i) => [
      i + 1, r.companyName || "", r.plantName || "", r.centerName || "", r.routeNumber, r.routeName
    ])
    const csvContent = [headers, ...rows].map(r => r.map(v => `"${(v || "").toString().replace(/"/g, '""')}"`).join(",")).join("\n")
    const blob = new Blob([csvContent], { type: "text/csv" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a"); a.href = url; a.download = `routes_${new Date().toISOString().slice(0, 10)}.csv`
    a.click(); URL.revokeObjectURL(url)
  }

  return (
    <>
      <div className="dashBody">

        <div className="font-size-18 mb-0 dashHeader" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <h4 style={{ margin: 0 }}>Routes</h4>
            {canAdd(MID) && (
              <Link to="/add-route" title="Add Route"
                style={{ width: "32px", height: "32px", borderRadius: "4px", background: "#28a745", color: "#fff",
                  display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "14px", textDecoration: "none" }}>
                <i className="fas fa-plus"></i>
              </Link>
            )}
            {canDelete(MID) && (
              <button title={selectedCount > 0 ? `Delete ${selectedCount} selected` : "Select items to delete"}
                onClick={() => { if (selectedCount > 0) setconfirm_multiple_alert(true); else alert("Please select at least one route to delete.") }}
                style={{ width: "32px", height: "32px", borderRadius: "4px", background: "#dc3545", color: "#fff",
                  border: "none", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "14px", cursor: "pointer", opacity: selectedCount > 0 ? 1 : 0.7 }}>
                <i className="fas fa-trash"></i>
              </button>
            )}
            <button title="Toggle Filter" onClick={removeFilter}
              style={{ width: "32px", height: "32px", borderRadius: "4px", background: "#0d6efd", color: "#fff",
                border: "none", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "14px", cursor: "pointer" }}>
              <i className="fas fa-filter"></i>
            </button>
            <BulkImportExport
              data={routes}
              columns={[
                { key: "chillingCenter", label: "Chilling Center ID", required: true },
                { key: "routeName", label: "Route Name", required: true },
                { key: "routeNumber", label: "Route Number", required: true },
              ]}
              sampleData={[{
                chillingCenter: "1", routeName: "Sample Route", routeNumber: "R001"
              }]}
              apiEndpoint="create-route"
              entityName="Routes"
              onImportComplete={fetchData}
              showExport={canExport(MID)}
              showImport={canAdd(MID)}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center" }}>
            <input type="text" className="form-control form-control-sm" placeholder="Search"
              style={{ width: "180px", borderRadius: "4px 0 0 4px" }}
              value={search} onChange={e => setSearch(e.target.value)} />
            <button className="btn btn-primary btn-sm" style={{ borderRadius: "0 4px 4px 0", height: "31px" }}>
              <i className="fas fa-search"></i>
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <Row className="mb-3 mt-2">
          {summaryCards.map((card, i) => (
            <Col md={3} sm={6} key={i}>
              <div style={{
                background: "#fff", borderRadius: "10px", padding: "14px 16px",
                borderLeft: `4px solid ${card.color}`, boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px"
              }}>
                <div style={{ width: "40px", height: "40px", borderRadius: "8px", background: card.color + "15",
                  display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <i className={`fas ${card.icon}`} style={{ color: card.color, fontSize: "16px" }}></i>
                </div>
                <div>
                  <div style={{ fontSize: "20px", fontWeight: "700", color: "#1e293b" }}>{card.value}</div>
                  <div style={{ fontSize: "11px", color: "#6c757d", textTransform: "uppercase", letterSpacing: "0.5px" }}>{card.label}</div>
                </div>
              </div>
            </Col>
          ))}
        </Row>

        <div className="table-responsive">
          <Table className="table mb-0 dairyTbl" id="routesTbl">
            <thead>
              <tr>
                <th><input type="checkbox" checked={parentChecked} onChange={handleParentCheckbox} /></th>
                <th>#</th>
                <th>Company</th>
                <th>Plant</th>
                <th>Chilling Center</th>
                <th>Route Number</th>
                <th>Route Name</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {showFilter && (
                <tr>
                  <td></td><td></td>
                  <td>
                    <select className="form-control form-control-sm" value={filterCompany}
                      onChange={e => setFilterCompany(e.target.value)}>
                      <option value="">All</option>
                      {uniqueCompanies.map((c, i) => <option key={i} value={c}>{c}</option>)}
                    </select>
                  </td>
                  <td>
                    <select className="form-control form-control-sm" value={filterPlant}
                      onChange={e => setFilterPlant(e.target.value)}>
                      <option value="">All</option>
                      {uniquePlants.map((p, i) => <option key={i} value={p}>{p}</option>)}
                    </select>
                  </td>
                  <td>
                    <select className="form-control form-control-sm" value={filterCC}
                      onChange={e => setFilterCC(e.target.value)}>
                      <option value="">All</option>
                      {uniqueCCs.map((c, i) => <option key={i} value={c}>{c}</option>)}
                    </select>
                  </td>
                  <td><input type="text" className="form-control form-control-sm" placeholder="Number..."
                    value={filterRouteNumber} onChange={e => setFilterRouteNumber(e.target.value)} /></td>
                  <td><input type="text" className="form-control form-control-sm" placeholder="Name..."
                    value={filterRouteName} onChange={e => setFilterRouteName(e.target.value)} /></td>
                  <td></td>
                </tr>
              )}
              {!dataLoaded ? (
                <tr><td colSpan={8}><img className="loaderImg" src={loader} alt="Loading" /></td></tr>
              ) : filtered.length > 0 ? (
                filtered.map((route, i) => (
                  <tr key={route.id}>
                    <td><input type="checkbox" checked={!!checkedItems[route.id]} onChange={() => handleCheckbox(route.id)} /></td>
                    <td>{i + 1}</td>
                    <td><span style={{ background: '#22c55e15', color: '#22c55e', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>{route.companyName || 'Unassigned'}</span></td>
                    <td><span style={{ background: '#6366f115', color: '#6366f1', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>{route.plantName || 'Unassigned'}</span></td>
                    <td><span style={{ background: '#0ea5e915', color: '#0ea5e9', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>{route.centerName || 'Unassigned'}</span></td>
                    <td>{route.routeNumber}</td>
                    <td style={{ fontWeight: "600" }}>{route.routeName}</td>
                    <td>
                      <div style={{ display: "flex", gap: "4px" }}>
                        {canEdit(MID) && <Link title="Edit" to={"/edit-route/" + route.id}
                          style={{ width: "28px", height: "28px", borderRadius: "4px", background: "#0d6efd", color: "#fff",
                            display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "12px" }}>
                          <i className="fas fa-edit"></i></Link>}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan={8} className="text-center py-4">
                  <i className="fas fa-route fa-2x text-muted mb-2 d-block"></i>No routes found
                </td></tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>

      {confirm_alert && (
        <SweetAlert title="Are you sure?" warning showCancel confirmBtnBsStyle="success" cancelBtnBsStyle="danger"
          onConfirm={() => { handleDelete(); setconfirm_alert(false); setsuccess_dlg(true); setdynamic_title("Deleted"); setdynamic_description("Route deleted.") }}
          onCancel={() => setconfirm_alert(false)}>You won't be able to revert this!</SweetAlert>
      )}
      {confirm_multiple_alert && (
        <SweetAlert title={`Delete ${selectedCount} routes?`} warning showCancel confirmBtnBsStyle="success" cancelBtnBsStyle="danger"
          onConfirm={() => { handleMultiDelete(); setconfirm_multiple_alert(false); setsuccess_dlg(true); setdynamic_title("Deleted"); setdynamic_description("Routes deleted.") }}
          onCancel={() => setconfirm_multiple_alert(false)}>You won't be able to revert this!</SweetAlert>
      )}
      {success_dlg && (
        <SweetAlert success title={dynamic_title} onConfirm={() => setsuccess_dlg(false)}>{dynamic_description}</SweetAlert>
      )}
    </>
  )
}

export default Routes
